// poiesis/src/views/registry.ts
// View-renderer registry — keyed by layout_kind (cartesian/table/geo/hierarchy/interval/board).
// DISTINCT from the cell widget registry (poiesis/src/widgets/registry.ts), which is keyed
// by attribute kind and renders cells. This one renders whole canvases.
import type { ComponentType } from 'react'
import type { ReducedFrame } from './contract'
import type { RecordGroup } from '../api/client'
import type { SelectionSet } from '../collection/selection'

export interface IdentityRenderInput {
  mode: 'identity'
  collection: {
    slug: string
    typeId: string
    filter?: unknown
    sort?: unknown
    hideToolbar?: boolean
    /** Suppress shared-view writes for fixed or permission-limited hosts. */
    readOnly?: boolean
    /** Host-owned reason for rendering a focusable disabled add-column action. */
    viewWritesDisabledReason?: string
    /** SEN-1411: density data budget on the identity path (first N rows + note). */
    maxRows?: number
    /** SEN-698 G-IA: controlled sort from the App unified strip. */
    externalSort?: unknown[]
    onSortChange?: (keys: unknown[]) => void
    /** SEN-698 G-IA: controlled filter tree from the App unified strip. */
    externalFilterTree?: unknown
    onFilterTreeChange?: (tree: unknown) => void
    /** Called when the user clicks the underlined primary cell to open the record detail panel. */
    onOpenRecord?: (recordId: string) => void
    /** The table's selection as a SET DESCRIPTION (collection/selection.ts). */
    onSelectionChange?: (selection: SelectionSet) => void
    /** SEN-169: the collection publishes its OWN `total_count` here. The MRL
     *  read the table already makes carries the total (limit-independent —
     *  `meta.pagination.total` in monad/api/v1/records.py), so a host that
     *  needs the record count subscribes to it instead of issuing a second
     *  `records/query`. `null` while the count is unresolved (type/filter
     *  switch in flight) — hosts render nothing rather than a stale number. */
    onTotal?: (total: number | null) => void
    /** SEN-1731 ("the strip IS the declaration"): the strip's resolved window,
     *  present only when the host decided the chip applies (App.tsx
     *  `showsPeriod` — a table whose visible columns include a windowed
     *  calculated attribute). Threaded into the calculated-columns batch
     *  request; absent for a table with no windowed column, which keeps the
     *  endpoint's own default. */
    period?: { period_start: string; period_end: string }
    /** Task 7: record ids CollectionTable should visually mark as pinned/highlighted
     *  (the master filter's pivot record, GAP D). Declared here so the identity
     *  and frame render-input shapes stay consistent; the consumer lands in Task 7. */
    highlightRecordIds?: string[]
  }
  bindings?: Record<string, unknown>
  viewKind?: string
  config?: Record<string, unknown>
}
/** F1 (SEN-1398): the interaction seam. Optional on every frame render — hosts
 *  that pass nothing get the previous read-only behavior; renderers surface
 *  gestures ONLY through these callbacks (no renderer-owned navigation/writes). */
export interface ViewInteractions {
  /** rowKey = the row's `<dims[0]>__ref` value when the engine's kenning remap
   *  carried one (reference dims: the RAW record id, never displayed), else the
   *  display category. Hosts may open the record peek on record-id rowKeys.
   *  Gantt passes row.id when present. Pinned so F2 consumers don't guess. */
  onSelect?: (rowKey: string, row: Record<string, unknown>) => void
  onDrill?: (picks: Record<string, string>) => void
  onMutate?: (patch: { rowKey: string; attribute: string; value: unknown }) => void
}

/** A record field a card-shaped renderer (the board) shows beside the card's
 *  label: `key` is the frame row's column, `kind`/`config` the attribute's (the
 *  cell widget registry draws it, as a table cell), `place` where on the card.
 *  `danger` marks a value the host judges urgent (an overdue due date). */
export interface CardField {
  key: string
  kind: string
  label: string
  config?: Record<string, unknown>
  place: 'head' | 'aside' | 'foot'
  danger?: (row: Record<string, unknown>) => boolean
}

/** One lane a host hands a board: the group's key as the group read returns it
 *  (an option id, a record id; null = no value), its label, its count. */
export interface BoardLane { key: string | null; label: string; count?: number }

/** An extra question a frame renderer asks UNDER ITS WIDGET'S scope and
 *  period (a map popup's energy block): a scalar over these metric slugs,
 *  optionally compared, AND-narrowed by `filter`. Compiled by the host through
 *  the one compile (engine.tsx buildQuestions) and run through the same
 *  reduce + batch path; the renderer never calls the API itself. Each metric's
 *  value comes back in the row under `measureColumn(slug)` (its own slug). */
export interface AskQuestion {
  measures: string[]
  /** answer the measures in this registered unit (the server converts; Measure.unit) */
  unit?: string
  compare?: { kind: 'previous_period' | 'same_period_last_year' | 'previous_days' | 'same_days_last_year' }
  filter?: unknown
}

export interface FrameRenderInput {
  mode: 'frame'
  /** The reduce answer. A widget that asks only a records question (a
   *  records-only stat) gets an empty frame: no rows, no dimensions. */
  frame: ReducedFrame
  /** Which questions the widget asked: a records-only widget (no reduce) is
   *  handed an empty frame, and draws its figure from `groups`. */
  asked?: { reduce: boolean; records: boolean }
  /** The widget's records question's answer (`bind.records`): one total group
   *  without group_by, else one per distinct key. Absent when not asked. */
  groups?: RecordGroup[]
  /** The records question's group_by paths — `groups[i].key[j]` is the value of `groupBy[j]`. */
  groupBy?: string[]
  /** Run an extra question under the widget's scope — see AskQuestion. */
  ask?: (question: AskQuestion) => Promise<ReducedFrame>
  /** The question is in flight. Only a kind that declares `renders_pending`
   *  is mounted then (the frame is empty); it draws its own pending state. */
  pending?: boolean
  /** The question was refused. Only a `renders_pending` kind receives it;
   *  others get the host's generic error line. */
  error?: unknown
  /** The widget's title, for a `self_framed` renderer that draws its own heading. */
  title?: string
  /** The referenced records' chips (kenning, identity icon, colour) by record id
   *  — the frame's reference dims and the records question's reference keys. */
  chips?: Record<string, { kenning: string; icon: string | null; color: string | null }>
  /** The cells a template option names (OptionField.template_of), by record id
   *  then attribute slug — one records/values read by the host. */
  cells?: Record<string, Record<string, unknown>>
  /** The kind's density draws to the card's edges (DensityPolicy.edge_to_edge):
   *  the renderer drops its own frame (border, radius) — the host's card is it. */
  edgeToEdge?: boolean
  /** A18: the renderer reports how many rows it DRAWS (the same callback the
   *  identity path publishes its total through) — a card's badge reads it. */
  onTotal?: (total: number | null) => void
  bindings: Record<string, unknown>
  roleNames?: Record<string, string>
  /** SEN-730 (M9): per-role served `consumes_subfields` (roleName → sub-field
   *  names), threaded from the view-kind spec so a renderer can reconstruct the
   *  EXACT composite dim_cell columns buildReduceRequest emitted (e.g. the geo
   *  renderer's "<positionSlug>.latitude"/".longitude"). Generic — any composite
   *  role on any renderer, no per-kind branch. */
  roleSubfields?: Record<string, string[]>
  viewKind?: string
  config?: Record<string, unknown>
  interactions?: ViewInteractions
  /** Fields a card-shaped renderer shows on each card (absent = the label only). */
  cardFields?: CardField[]
  /** A board's lanes, decided by the host (Work: the grouped list's groups);
   *  absent = one lane per distinct value. */
  lanes?: BoardLane[]
  /** Values a card may still move to that have no lane ("Move to → More"). */
  moreLanes?: BoardLane[]
  /** Why the lanes cannot be written by a drop (a hop path); cards drag, lanes refuse. */
  lanesReadOnly?: string
  /** SEN-1424: the bound attribute TYPE per role (roleName → attribute type,
   *  e.g. { date: "timestamp" }). Metadata-driven — lets a renderer decide
   *  placement (timestamp → time-positioned, date → full-day) without sniffing
   *  the value ("no midnight guessing"). Comes from the host's attribute
   *  catalog; absent keys = type unknown, renderer picks a safe default. */
  roleKinds?: Record<string, string>
  /** The bound attribute's `config` per role (roleName → config), beside
   *  `roleKinds`: lets a renderer hand a role's value to that kind's widget
   *  with the same config a table cell gets (e.g. a select's declared options,
   *  so its colour matches — widgets/optionColor). Absent = no config. */
  roleConfigs?: Record<string, Record<string, unknown>>
  /** SEN-1769: the view kind's declared per-axis scale origin ({'x'|'y':
   *  'zero'|'data'}), threaded from the served spec by ViewHost — the same
   *  metadata-in, no-kind-branch route `roleSubfields` and `roleKinds` take.
   *  Absent axis = the historical zero origin. */
  axisBaseline?: Record<string, 'zero' | 'data'>
  /** SEN-1424: the renderer's config-write seam. A renderer that lets the user
   *  change a SETTING (e.g. calendar `scale`) reports the PATCH up to the host,
   *  which persists it to the view/widget config. Absent = settings read-only. */
  onConfigChange?: (patch: Record<string, unknown>) => void
  /** SEN-1411 (T6/F3): which surface this frame renders on. Renderers adapt
   *  CHROME (legend/toolbar/axes) from `chrome`, never data — the host already
   *  applied the data budget. Absent = full (legacy callers). */
  density?: 'full' | 'peek' | 'block'
  chrome?: string[]
  /** 2026-09-02: the window the frame was reduced over, as the host requested
   *  it. The frame's meta does not carry it, and a stat tile that shows a
   *  number without saying which period it covers is a plausible-but-
   *  meaningless number. ISO strings; absent for identity frames. */
  period?: { period_start: string; period_end: string; label?: string }
  /** Owner 2026-10-30 ("yes to drag to zoom"): the zoom seam. A time-axis
   *  renderer that lets the reader drag across its x axis reports the chosen
   *  window ([start, end), ISO) up to the host, which asks for it (lazily, in
   *  chunks) and draws the reset. Absent = the axis does not zoom. */
  onZoom?: (window: { period_start: string; period_end: string }) => void
}
export type ViewRendererProps = IdentityRenderInput | FrameRenderInput

/** Single row produced by the reduce engine — an open key→value dictionary.
 *  Use this instead of `any` in frame renderers to avoid erasing the type
 *  contract while keeping the open shape the reduce path requires. */
export type FrameRow = Record<string, unknown>

/** Facts about a renderer that a caller would otherwise have to infer from the
 *  layout_kind NAME. `needsCollection` is the first: a table renders from a
 *  live collection (mode="identity"), everything else renders from a reduced
 *  frame. That was previously a `layout_kind === 'table'` branch in every
 *  caller — and the copy in gallery/ViewGallery.tsx had already drifted from
 *  the one in engine.tsx, which additionally treats a RANKED table as a frame.
 *  The fact belongs on the registration, not in each caller's if. */
export interface ViewRendererOptions {
  needsCollection?: boolean
}

const _renderers = new Map<string, ComponentType<ViewRendererProps>>()
const _options = new Map<string, ViewRendererOptions>()

export function registerViewRenderer(
  layoutKind: string,
  component: ComponentType<ViewRendererProps>,
  options: ViewRendererOptions = {},
): void {
  if (_renderers.has(layoutKind)) throw new Error(`view renderer for layout_kind '${layoutKind}' already registered`)
  _renderers.set(layoutKind, component)
  _options.set(layoutKind, options)
}
export function resolveViewRenderer(layoutKind: string): ComponentType<ViewRendererProps> | undefined {
  return _renderers.get(layoutKind)
}
export function rendererOptions(layoutKind: string): ViewRendererOptions {
  return _options.get(layoutKind) ?? {}
}
/** Test-only: reset the registry between cases. */
export function _clearViewRenderers(): void {
  _renderers.clear()
  _options.clear()
}
